import React from 'react';
import {
  ArrowRightIcon,
  Box,
  Button,
  ExternalLinkIcon,
  InfoIcon,
  Link,
  RefreshIcon,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TablePagination,
  TableRow,
  TableToolbar,
  TableToolbarActions,
  Text,
  Tooltip,
  TooltipInteractiveWrapper,
} from '@razorpay/blade/components';
import { routePaths } from 'components/Routes/data';
import { StatusBadge } from 'components/SalaryComponents/components/StatusBadge';
import {
  helpArticles,
  perquisitesFiltersMap,
  salaryComponentParams,
} from 'components/SalaryComponents/constants';
import { useDetailsView } from 'components/SalaryComponents/hooks';
import { getTableItemCountLimits } from 'components/SalaryComponents/utils';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { ListViewLoader } from 'components/ui/ListViewLoader';
import { useFilters } from 'hooks/useFilters';
import { usePagination } from 'hooks/usePagination';
import { useHistory } from 'react-router-dom';
import { PerquisitesComponent } from 'api/gql-utils/perquisitesComponents/types';

const PerquisitesListView = ({
  data,
  isLoading,
  isRefreshing,
  isError,
  refetch,
  searchFilter,
  onSearchChange,
  onSearchClear,
}: {
  data: PerquisitesComponent[];
  isLoading: boolean;
  isRefreshing: boolean;
  isError: boolean;
  refetch?: () => void;
  searchFilter: string;
  onSearchChange: (value: string) => void;
  onSearchClear: () => void;
}) => {
  const history = useHistory();
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });

  const { openDetailsView } = useDetailsView(salaryComponentParams.componentId);

  const isTableEmpty = data.length === 0;
  const errorString = isError ? 'Failed to load data' : '';
  const { isAnyFilterApplied, currentFilters } = useFilters({
    filtersMap: perquisitesFiltersMap,
    onParamsChange(params) {
      setCurrentPage(0);
    },
  });

  return (
    <>
      <Table
        data={{ nodes: data }}
        pagination={
          !isTableEmpty ? (
            <TablePagination
              currentPage={currentPage}
              showPageSizePicker={true}
              onPageChange={({ page }) => {
                setCurrentPage(page);
              }}
              onPageSizeChange={({ pageSize }) => {
                setItemsPerPage(pageSize);
              }}
              totalItemCount={data.length}
            />
          ) : undefined
        }
        toolbar={
          <TableToolbar title={getTableItemCountLimits(data.length, currentPage, itemsPerPage)}>
            <TableToolbarActions>
              <Box
                display="flex"
                flexDirection="row"
                gap="spacing.4"
                alignItems="center"
                justifyContent={'end'}>
                <Box width="250px">
                  <SearchInput
                    label=""
                    placeholder="Search Component"
                    value={searchFilter}
                    onChange={({ value }) => {
                      onSearchChange(value || '');
                      setCurrentPage(0);
                    }}
                    onClearButtonClick={() => {
                      onSearchClear();
                      setCurrentPage(0);
                    }}
                  />
                </Box>
                <Button icon={RefreshIcon} variant={'tertiary'} onClick={refetch ?? (() => {})} />
              </Box>
            </TableToolbarActions>
          </TableToolbar>
        }
        showBorderedCells={false}>
        {(salaryComponents) => (
          <>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>
                  <Text weight="semibold">Component name</Text>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Box display={'flex'} flexDirection={'row'} gap={'spacing.3'}>
                    <Text weight="semibold">Display name</Text>
                    <Tooltip
                      placement="top"
                      content={`The name that will appear in employee's payslip and Salary Register`}>
                      <TooltipInteractiveWrapper>
                        <InfoIcon marginTop={'spacing.1'} />
                      </TooltipInteractiveWrapper>
                    </Tooltip>
                  </Box>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Box marginX={'auto'}>
                    <Text weight="semibold">Status</Text>
                  </Box>
                </TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            {isLoading || isRefreshing ? (
              <ListViewLoader columnCount={3} accessibilityLabel="loading perquisites components" />
            ) : (
              <TableBody>
                {salaryComponents.map((salaryComponent) => {
                  return (
                    <TableRow
                      key={salaryComponent.id}
                      item={salaryComponent}
                      onClick={() => openDetailsView(salaryComponent.id)}
                      hoverActions={
                        <Button
                          variant="tertiary"
                          color="primary"
                          size="medium"
                          icon={ArrowRightIcon}
                          iconPosition="right"
                          onClick={() => openDetailsView(salaryComponent.id)}
                          testID={`view-details-button-${salaryComponent.id}`}>
                          View Details
                        </Button>
                      }>
                      <TableCell>
                        <Text>{salaryComponent.name}</Text>
                      </TableCell>
                      <TableCell>
                        <Text>{salaryComponent.displayName}</Text>
                      </TableCell>
                      <TableCell>
                        <Box marginX={'auto'}>
                          <StatusBadge isActive={salaryComponent.status === 'active'} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </>
        )}
      </Table>
      {isTableEmpty && !isLoading && !isRefreshing && !isError && (
        <EmptyListView
          title={
            isAnyFilterApplied
              ? 'No Search Results Found'
              : 'No Perquisites components created yet 🙈'
          }
          description={
            <Text color={'surface.text.gray.muted'} weight={'medium'} textAlign={'center'}>
              Click on Create Component button to create your first component
            </Text>
          }>
          <Box display={'flex'} flexDirection={'column'} gap={'spacing.4'} marginX={'auto'}>
            <Link
              size="medium"
              variant="anchor"
              href={helpArticles.learnMore} // link to be added
              icon={ExternalLinkIcon}
              iconPosition="right"
              target="_blank">
              Learn more about salary component
            </Link>
            <Box marginX={'auto'}>
              <Button
                size="medium"
                onClick={() => {
                  history.push(routePaths.salaryComponents.create.perquisites.root);
                }}
                isFullWidth>
                Create Perquisites Component
              </Button>
            </Box>
          </Box>
        </EmptyListView>
      )}
      {isError && (
        <EmptyListView
          title={`There was an error while fetching the data for ${
            currentFilters.status ?? 'all'
          } perquisites components. `}
          isError
          description={errorString ? `Error: ${errorString}` : null}>
          <Box display={'flex'} flexDirection={'column'} gap={'spacing.4'} marginX={'auto'}>
            <Link
              size="medium"
              variant="anchor"
              href={helpArticles.learnMore} // link to be added
              icon={ExternalLinkIcon}
              iconPosition="right"
              target="_blank">
              Learn more about salary component
            </Link>
            <Box marginX={'auto'}>
              <Button
                size="medium"
                onClick={refetch}
                isFullWidth
                iconPosition="left"
                icon={RefreshIcon}>
                Retry
              </Button>
            </Box>
          </Box>
        </EmptyListView>
      )}
    </>
  );
};

export default PerquisitesListView;
